import { intersects } from 'ol/extent';
import GeoJSON from 'ol/format/GeoJSON.js';
import { Style, Fill, Stroke, RegularShape, Circle as CircleStyle, Text } from 'ol/style';
import { MultiPoint, Point } from 'ol/geom';

import { ANNOTATION_BOX_FILTER } from '@utils/constants';

import { calculateGeometryCenter } from '../helpers';

export let activeSquareRef = null;
export let annotationClassId = null;
/** The taxonomy node new boxes are drawn for: `{ uuid, color }`, or null outside annotation mode. */
export let annotationNode = null;
/** The vector source the boxes of the running session live in, or null between sessions. */
export let annotationSource = null;
/** The label new boxes get (free text, null = the object's default). */
export let annotationLabel = null;
/** The box the user is moving or reshaping, by `boxId`; chips relabel it. */
export let selectedBoxId = null;
const selectionListeners = new Set();

/**
 * Whether the running session is the scoped object session (Collection view):
 * handles only on the selected box, hover feedback and label text. Off for
 * every other flow, which keeps its styles as they were.
 */
let annotationScoped = false;
/** The box under the pointer in a scoped session, by `boxId`. */
let hoveredBoxId = null;
/** Theme values the label text takes (`fontFamily`, `fontSizeSM`, `colorText`, `colorBgContainer`). */
let annotationTheme = null;
/** Which boxes of an object session the map draws (an `ANNOTATION_BOX_FILTER` value). */
let boxFilter = ANNOTATION_BOX_FILTER.ALL;

/** Labels show once the map is zoomed in at least this far (map units per pixel). */
export const LABEL_MAX_RESOLUTION = 0.5;
const LABEL_MAX_CHARS = 24;
/** The zoom limit in force: a session whose map units are not metres sets its own. */
let labelMaxResolution = LABEL_MAX_RESOLUTION;

const BOX_DASH = [10, 8];

/**
 * Whether a box of an object session is drawn dashed: a human row, or a model
 * box the user touched (it becomes an `edit`). Untouched model boxes and the
 * detections of a Model view session are solid.
 */
export const isDashedBox = feature => {
	if (!feature?.get?.('nodeUuid')) return false;
	return feature.get('source') !== 'model' || feature.get('saved') === false;
};

/**
 * Whether the box filter lets a box show. Only the boxes of an object session
 * are filtered: a model box is a detection, every other row an annotation. A
 * box the user is working on (unsaved, or selected) always shows. A hidden box
 * has no style, so the map's hit detection cannot select, move or erase it; it
 * stays in the source, and a tile save still sends it.
 */
export const isBoxShown = feature => {
	if (boxFilter === ANNOTATION_BOX_FILTER.ALL || !feature?.get?.('nodeUuid')) return true;
	if (!feature.get('saved') || feature.get('boxId') === selectedBoxId) return true;
	const isDetection = feature.get('source') === 'model';
	return boxFilter === ANNOTATION_BOX_FILTER.DETECTIONS ? isDetection : !isDetection;
};

const BOX_FILTERS = Object.values(ANNOTATION_BOX_FILTER);

/** Draw every box, only the annotations, or only the detections (an `ANNOTATION_BOX_FILTER` value; anything else means All). */
export const setBoxFilter = filter => {
	boxFilter = BOX_FILTERS.includes(filter) ? filter : ANNOTATION_BOX_FILTER.ALL;
	annotationSource?.changed();
};

export const resetAnnotationRefs = () => {
	activeSquareRef = null;
	annotationClassId = null;
	annotationNode = null;
	annotationSource = null;
	annotationLabel = null;
	selectedBoxId = null;
	annotationScoped = false;
	hoveredBoxId = null;
	boxFilter = ANNOTATION_BOX_FILTER.ALL;
	labelMaxResolution = LABEL_MAX_RESOLUTION;
};

/**
 * Show box labels from this zoom (map units per pixel) up; `null` goes back to
 * `LABEL_MAX_RESOLUTION`. That limit is sized for an orthophoto, where a unit is a
 * metre: a single image's units are image pixels, so there it would hide every
 * label at the zoom the whole image fits in, and the session lifts it instead.
 */
export const setAnnotationLabelMaxResolution = max => {
	labelMaxResolution = max ?? LABEL_MAX_RESOLUTION;
	annotationSource?.changed();
};

export const setAnnotationScoped = scoped => {
	annotationScoped = Boolean(scoped);
	annotationSource?.changed();
};

/** Hand the label text its font and colours (from `theme.useToken()` in a component). */
export const setAnnotationTheme = theme => {
	annotationTheme = theme ?? null;
	annotationSource?.changed();
};

/** The box under the pointer; redraws only when it changes. */
export const setHoveredBox = boxId => {
	const next = boxId ?? null;
	if (next === hoveredBoxId) return;
	hoveredBoxId = next;
	annotationSource?.changed();
};

// function that returns true if the point is inside the square using black magic
export const isPointInsideSquare = (point, square) => {
	const squareCoordinates = square.geometry.coordinates[0];
	const x = point[0];
	const y = point[1];

	const x1 = squareCoordinates[0][0];
	const y1 = squareCoordinates[0][1];
	const x2 = squareCoordinates[1][0];
	const y2 = squareCoordinates[1][1];
	const x3 = squareCoordinates[2][0];
	const y3 = squareCoordinates[2][1];
	const x4 = squareCoordinates[3][0];
	const y4 = squareCoordinates[3][1];

	const d1 = (x - x1) * (y2 - y1) - (y - y1) * (x2 - x1);
	const d2 = (x - x2) * (y3 - y2) - (y - y2) * (x3 - x2);
	const d3 = (x - x3) * (y4 - y3) - (y - y3) * (x4 - x3);
	const d4 = (x - x4) * (y1 - y4) - (y - y4) * (x1 - x4);

	const hasNeg = d1 < 0 || d2 < 0 || d3 < 0 || d4 < 0;
	const hasPos = d1 > 0 || d2 > 0 || d3 > 0 || d4 > 0;

	return !(hasNeg && hasPos);
};

export const isFeatureIntersectingSquare = (feature, square) => {
	if (!feature || !square) {
		console.warn('Invalid input: feature and square are required.');
		return false;
	}

	// Convert square to feature
	let squareFeature;
	try {
		squareFeature = new GeoJSON().readFeature(square);
	} catch (error) {
		console.error('Error reading square feature:', error);
		return false;
	}

	// Get bounding boxes of geometries
	let extent1, extent2;
	try {
		extent1 = feature.getGeometry
			? feature.getGeometry()?.getExtent()
			: getExtentFromCoordinates(feature.coordinates);
		extent2 = squareFeature.getGeometry()?.getExtent();
	} catch (error) {
		console.error('Error getting extents:', error);
		return false;
	}

	// Check if bounding boxes intersect
	const isIntersecting = extent1 && extent2 && intersects(extent1, extent2);

	return isIntersecting;
};

// Utility function to calculate extent from coordinates
const getExtentFromCoordinates = coordinates => {
	if (!coordinates || coordinates.length === 0) return null;

	let minX = coordinates[0][0];
	let minY = coordinates[0][1];
	let maxX = coordinates[0][0];
	let maxY = coordinates[0][1];

	coordinates.forEach(([x, y]) => {
		if (x < minX) minX = x;
		if (y < minY) minY = y;
		if (x > maxX) maxX = x;
		if (y > maxY) maxY = y;
	});

	return [minX, minY, maxX, maxY];
};

/**
 * Whether a box of an object session belongs to the open tile. A box is saved
 * with the tile it is filed under, and a tile save sends only that tile's boxes;
 * tiles can overlap, so a box filed under a neighbouring tile is shown but left
 * alone here (edited from its own tile). Outside a scoped session, and for a box
 * or tile without a name, there is nothing to tell.
 */
const isFiledInOpenTile = feature => {
	if (!annotationScoped) return true;
	const openTile = activeSquareRef?.properties?.tile_name;
	const tile = feature.get('tileName');
	return !openTile || !tile || tile === openTile;
};

/**
 * A box of the active object, filed under and lying in the open tile (or image):
 * the only kind a scoped session lets the user select, move or reshape.
 */
export const isSelectableBox = feature =>
	Boolean(
		feature &&
			annotationNode &&
			activeSquareRef &&
			feature.get('nodeUuid') === annotationNode.uuid &&
			isFiledInOpenTile(feature) &&
			isFeatureIntersectingSquare(feature, activeSquareRef)
	);

export const setSquareBoundary = activeSquare => {
	if (activeSquare !== undefined) {
		activeSquareRef = activeSquare;
	}
	activeSquareRef = activeSquare;
};

export const addAnnotationId = classId => {
	annotationClassId = classId;
};

/**
 * Make `node` (a taxonomy node, or null) the object new boxes are drawn for.
 * Kept outside React because the draw interactions read it at draw time.
 */
export const setAnnotationNode = node => {
	annotationNode = node ? { uuid: node.uuid, color: node.color } : null;
	// The styles mute the boxes of other objects, so a new active object restyles the map.
	annotationSource?.changed();
};

/** Register (or, with null, forget) the live source of the session, for the session helpers. */
export const setAnnotationSource = source => {
	annotationSource = source;
};

/** The label stamped on the next boxes (null = default). */
export const setAnnotationLabel = label => {
	annotationLabel = label ?? null;
};

/**
 * The box the user started moving or reshaping (null when a draw starts or a
 * session ends). Listeners get its `boxId`; the styles draw it bolder.
 */
export const selectBox = boxId => {
	selectedBoxId = boxId ?? null;
	selectionListeners.forEach(listener => listener(selectedBoxId));
	annotationSource?.changed();
};

export const notifyBoxSelected = feature => {
	const boxId = feature?.get?.('boxId') ?? null;
	if (boxId === selectedBoxId) return;
	selectBox(boxId);
};

/** Hear about the selected box; returns the unsubscribe. */
export const subscribeBoxSelection = listener => {
	selectionListeners.add(listener);
	return () => selectionListeners.delete(listener);
};

// Identity a box keeps across the clones the session store holds, so a saved
// tile can be compared with the live one.
let nextBoxNumber = 0;
const newBoxId = () => `box-${Date.now().toString(36)}-${(nextBoxNumber += 1)}`;

/**
 * Write the active node onto a feature being drawn, so it keeps its object and
 * colour after the active node changes. Silent: the draw sketch restyles anyway.
 */
export const stampAnnotationNode = feature => {
	if (!annotationNode || typeof feature?.set !== 'function') return;
	feature.set('nodeUuid', annotationNode.uuid, true);
	feature.set('nodeColor', annotationNode.color, true);
	if (!feature.get('boxId')) feature.set('boxId', newBoxId(), true);
	feature.set('label', annotationLabel, true);
};

/** `#RRGGBB` -> `{ r, g, b }`, or null for anything else (the palette then applies). */
export const nodeColorToRgb = hex => {
	const match = /^#?([0-9a-f]{6})$/i.exec(hex ?? '');
	if (!match) return null;
	const value = parseInt(match[1], 16);
	return { r: (value >> 16) & 255, g: (value >> 8) & 255, b: value & 255 };
};

/**
 * The colour a feature is drawn in: the node it was drawn for; for the draw
 * sketch (no node, no class yet) the active node; else the class palette entry,
 * which is what a detection loaded from the model gets.
 */
export const annotationColor = ({ feature, classId, colorOptions }) => {
	const nodeColor =
		feature?.get?.('nodeColor') ??
		(feature?.get?.('classId') === undefined ? annotationNode?.color : undefined);
	return (
		nodeColorToRgb(nodeColor) ?? colorOptions[classId % Object.keys(colorOptions).length] ?? {}
	);
};

const getGeometry = feature => {
	const modifyGeometry = feature.get('modifyGeometry');
	return modifyGeometry ? modifyGeometry.geometry : feature.getGeometry();
};

const strokeWidthOf = feature => {
	const boxId = feature.get?.('boxId');
	if (selectedBoxId && boxId === selectedBoxId) return 5;
	if (annotationScoped && hoveredBoxId && boxId === hoveredBoxId) return 4;
	return 3;
};

/** The label of a selectable box, at its top-left corner; null when none applies. */
const labelStyleOf = ({ feature, geometry, resolution, isSelectable }) => {
	const label = feature.get?.('label');
	if (!annotationScoped || !isSelectable || !label || !annotationTheme) return null;
	if (resolution === undefined || resolution > labelMaxResolution) return null;
	const [minX, , , maxY] = geometry.getExtent();
	const text = label.length > LABEL_MAX_CHARS ? `${label.slice(0, LABEL_MAX_CHARS - 1)}…` : label;
	const { fontFamily, fontSizeSM, colorText, colorBgContainer } = annotationTheme;
	return new Style({
		geometry: new Point([minX, maxY]),
		text: new Text({
			text,
			font: `${fontSizeSM}px ${fontFamily}`,
			fill: new Fill({ color: colorText }),
			backgroundFill: new Fill({ color: colorBgContainer }),
			padding: [2, 4, 2, 4],
			textAlign: 'left',
			textBaseline: 'top',
			offsetX: 6,
			offsetY: 6,
		}),
	});
};

/**
 * Annotation styles
 * @param {} feature
 * @param {*} colorOptions color options for the annotation
 * @returns feature styles
 */
export const annotationStyles = ({ feature, activeClassId = 0, colorOptions, resolution }) => {
	if (!isBoxShown(feature)) return [];

	const geometry = getGeometry(feature);
	const result = calculateGeometryCenter(geometry);

	const classId = feature.values_?.classId ?? activeClassId;
	const { r = 0, g = 0, b = 0 } = annotationColor({ feature, classId, colorOptions });
	// In an object session, the boxes of the other objects stay visible but muted
	// and out of reach of the modify and translate tools.
	const ownerUuid = feature.get?.('nodeUuid');
	const isOtherObject = Boolean(annotationNode && ownerUuid && ownerUuid !== annotationNode.uuid);
	let colorOpcacity = isOtherObject ? '0.3' : '0.5';

	// Style for the annotation
	const styles = [];

	const isInside =
		!isOtherObject &&
		isFiledInOpenTile(feature) &&
		(activeSquareRef ? isFeatureIntersectingSquare(feature, activeSquareRef) : true); // If no square is active, the feature is always inside

	if (!isInside) {
		// Silent: written while styling, it must not count as a change of the box
		// (the session re-checks Save on every change). Readers only `.get()` it.
		feature.set('isInside', false, true);
	} else {
		// Feature is inside and can be modified
		feature.set('isInside', true, true);
		colorOpcacity = '1';

		// Add draggable vertices: in a scoped session only the selected box has them
		let points;
		const showHandles = !annotationScoped || feature.get?.('boxId') === selectedBoxId;

		if (geometry.getType() === 'Circle') {
			const center = geometry.getCenter();
			const radius = geometry.getRadius();

			points = [];

			// Add points along the circumference
			const numPoints = 4; // Number of points along the circumference
			for (let i = 0; i < numPoints; i++) {
				const angle = (i * 2 * Math.PI) / numPoints;
				const x = center[0] + radius * Math.cos(angle);
				const y = center[1] + radius * Math.sin(angle);
				points.push([x, y]);
			}
		} else {
			const { coordinates, minRadius, sqDistances } = result;

			if (coordinates && sqDistances) {
				const rsq = minRadius * minRadius;

				// Filter coordinates based on the squared distance
				points = coordinates.filter((coordinate, index) => sqDistances[index] > rsq);

				// Calculate midpoints and add them to the points array @TODO: This could be used to add more vertices
				// for (let i = 0; i < coordinates.length; i++) {
				// 	const nextIndex = (i + 1) % coordinates.length;
				// 	const midpoint = [
				// 		(coordinates[i][0] + coordinates[nextIndex][0]) / 2,
				// 		(coordinates[i][1] + coordinates[nextIndex][1]) / 2,
				// 	];
				// 	points.push(midpoint);
				// }
			}
		}

		if (showHandles && points?.length) {
			// Create and push the new style
			styles.push(
				new Style({
					geometry: new MultiPoint(points),
					image: new RegularShape({
						radius: 5,
						points: 4,
						angle: Math.PI / 4,
						fill: new Fill({
							color: 'rgba(255, 255, 255, 0.7)',
						}),
						stroke: new Stroke({
							color: '#000',
							width: 1,
						}),
					}),
				})
			);
		}
	}

	// Style for the annotation
	styles.push(
		new Style({
			geometry: getGeometry,
			fill: new Fill({
				color: 'rgba(0, 0, 0, 0.25)',
			}),
			stroke: new Stroke({
				color: `rgba(${r}, ${g}, ${b}, ${colorOpcacity})`,
				// The box being moved or reshaped stands out, so the chips clearly relabel it.
				width: strokeWidthOf(feature),
				lineDash: isDashedBox(feature) ? BOX_DASH : undefined,
			}),
		})
	);

	const labelStyle = labelStyleOf({ feature, geometry, resolution, isSelectable: isInside });
	if (labelStyle) styles.push(labelStyle);

	// Add a Point style for the cursor
	styles.push(
		new Style({
			geometry: geometry.getType() === 'Point' ? geometry : null,
			image: new CircleStyle({
				radius: 6,
				fill: new Fill({
					color: `rgba(${r}, ${g}, ${b}, ${colorOpcacity})`,
				}),
				stroke: new Stroke({
					color: `rgba(255, 255, 255, 0.5)`,
					width: 2,
				}),
			}),
		})
	);

	return styles;
};
